import { useEffect } from "react";
import { Link, navigate } from "../app/router";
import { useSession } from "../app/session";
import { stickerUrl } from "../data/stickers";
import { Tape } from "../ui/kit";
import { startGuestBoard } from "./guest";

const FEATURES: { title: string; body: string; paper: string; tape: string; sticker: string; rot: number }[] = [
  { title: "Made for Apple Pencil", body: "Pressure-sensitive pens, palm rejection, hover preview. Fingers pan & pinch; two-finger tap to undo.", paper: "#f6dc8f", tape: "#d98c8c", sticker: "pencil", rot: -1.6 },
  { title: "Hold to snap", body: "Draw a wobbly circle, box or arrow and hold still — it straightens itself. Scribble over ink to erase.", paper: "#cfdcb6", tape: "#8aa3bf", sticker: "star", rot: 1.2 },
  { title: "An endless desk", body: "An infinite canvas with frames for pages. Zoom from the whole map down to a single word.", paper: "#c4d9e4", tape: "#d9b25a", sticker: "telescope", rot: -0.8 },
  { title: "Notes that link", body: "Markdown index cards with checklists, $math$ and [[links]] between boards — a sketchbook that thinks like a second brain.", paper: "#f3c0bb", tape: "#93ad86", sticker: "book-open", rot: 1.5 },
  { title: "Yours, everywhere", body: "A free account keeps your boards in sync live between iPad and laptop, and they still open offline.", paper: "#dacdea", tape: "#d0845f", sticker: "cloud", rot: -1.2 },
  { title: "Share & present", body: "Send a view or edit link, present frames as slides with a laser pointer, export PNG, SVG or PDF.", paper: "#f5c49a", tape: "#ab9ac7", sticker: "rocket", rot: 0.9 }
];

export function Landing() {
  const { user } = useSession();
  useEffect(() => {
    document.title = "Sepia — a warm, pen-first infinite canvas";
  }, []);
  return (
    <div className="landing">
      <header className="landing-nav">
        <Link to="/welcome" className="brand">
          <img src="/favicon-64.png" alt="" width="36" height="36" />
          <span className="wordmark">sepia</span>
        </Link>
        <nav>
          {user ? (
            <Link to="/" className="btn btn-primary">My boards</Link>
          ) : (
            <>
              <Link to="/login" className="nav-link">Log in</Link>
              <Link to="/signup" className="btn btn-primary">Sign up free</Link>
            </>
          )}
        </nav>
      </header>

      <main>
        <section className="hero">
          <div className="hero-copy">
            <p className="kicker">an infinite sketchbook</p>
            <h1>
              Think on paper
              <br />
              <em>that never ends.</em>
            </h1>
            <p className="lede">
              Sepia is a warm, pen-first whiteboard for iPad and the web. Sketch with your Apple Pencil, pin sticky notes and index cards,
              link ideas across boards — and pick up where you left off on any device.
            </p>
            <div className="hero-cta">
              <button type="button" className="btn btn-primary big" onClick={() => (user ? navigate("/new") : startGuestBoard())}>
                Start drawing{user ? "" : " — no sign-up"}
              </button>
              {!user && (
                <Link to="/signup" className="btn btn-plain big">
                  Create a free account
                </Link>
              )}
            </div>
            <p className="hand-note">
              <svg viewBox="0 0 120 40" width="96" height="32" aria-hidden="true">
                <path d="M4 30 C 30 36, 70 30, 104 10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                <path d="M92 8 L105 9 L100 21" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              sepia ink comes from cuttlefish. So does our mascot.
            </p>
          </div>
          <div className="hero-art">
            <div className="polaroid">
              <Tape color="#d98c8c" className="hero-tape-l" />
              <Tape color="#93ad86" className="hero-tape-r" />
              <img src="/art/mascot-hero.webp" alt="A cheerful cuttlefish drawing a spiral with a pencil" width="1100" height="613" />
              <span className="polaroid-cap">Sepia, drawing</span>
            </div>
          </div>
        </section>

        <section className="features" aria-label="What you can do">
          {FEATURES.map((f) => (
            <article key={f.title} className="feature-note" style={{ ["--paper" as string]: f.paper, transform: `rotate(${f.rot}deg)` }}>
              <Tape color={f.tape} className="feature-tape" />
              <img className="feature-sticker" src={stickerUrl(f.sticker)} alt="" width="84" height="84" loading="lazy" />
              <h2>{f.title}</h2>
              <p>{f.body}</p>
            </article>
          ))}
        </section>

        <section className="install">
          <div className="install-card">
            <Tape color="#d9b25a" className="install-tape" />
            <h2>Put it on your iPad’s home screen</h2>
            <ol>
              <li>Open this page in Safari.</li>
              <li>Tap the Share button, then “Add to Home Screen”.</li>
              <li>Sepia opens full-screen, like an app — Pencil ready.</li>
            </ol>
          </div>
        </section>
      </main>

      <footer className="landing-foot">
        <span>
          Made with care by <a href="https://sara-aminpour.com" target="_blank" rel="noopener">Sara Aminpour</a>
        </span>
        <span className="muted">Your boards are private unless you share them.</span>
      </footer>
    </div>
  );
}
